import React, { useState, useCallback, useEffect, useMemo } from "react";
import { Button, message, Form, Input, Switch, Row, Col, Select } from 'antd';
import { updateWebActionMapping } from "../../actions/operator-web-config.action";

const isBooleanActionKey = (key, item) =>
    (item && item.sub_category === 'Conditional') ||
    /^HIDE_|^SHOW_/i.test(key || '');

const coerceToBoolean = (value) => {
    if (value === 1 || value === '1' || value === true || value === 'true') return true;
    if (value === 0 || value === '0' || value === false || value === 'false') return false;
    return Boolean(value);
};

const normalizeInitialValues = (initialValues, webConfigActions = {}) => {
    if (!Array.isArray(initialValues)) {
        return initialValues || {};
    }

    const base = {};

    initialValues.forEach((entry) => {
        const key = entry?.web_action;
        if (!key) return;

        let value = entry.value;

        let meta;
        Object.keys(webConfigActions || {}).some((categoryKey) => {
            const found = (webConfigActions[categoryKey] || []).find(
                (item) => item.action_name === key
            );
            if (found) {
                meta = found;
                return true;
            }
            return false;
        });

        if (isBooleanActionKey(key, meta)) value = coerceToBoolean(value);

        base[key] = value;
    });

    return base;
};

const buildDefaultConfigFromActions = (webConfigActions = {}, initialValues = {}) => {
    const base = { ...normalizeInitialValues(initialValues, webConfigActions) };
    Object.keys(webConfigActions || {}).forEach((categoryKey) => {
        const items = webConfigActions[categoryKey] || [];
        items.forEach((item) => {
            const key = item.action_name;
            if (key == null || key === '') return;
            if (base[key] !== undefined) {
                if (isBooleanActionKey(key, item)) base[key] = coerceToBoolean(base[key]);
                return;
            }
            if (item.sub_category === 'Conditional' || /^HIDE_|^SHOW_/i.test(key)) {
                base[key] = false;
            } else {
                base[key] = '';
            }
        });
    });
    return base;
};

const formValuesToApiPayload = (formValues, webConfigActions, initialValues) => {
    if (!formValues || typeof formValues !== 'object') return [];

    let defaultOperatorCode = null;
    if (Array.isArray(initialValues) && initialValues.length > 0) {
        defaultOperatorCode = initialValues[0]?.operator_code || null;
    }


    return Object.keys(formValues).map((key) => {
        let value = formValues[key];

        let meta;
        Object.keys(webConfigActions || {}).some((categoryKey) => {
            const found = (webConfigActions[categoryKey] || []).find(
                (item) => item.action_name === key
            );
            if (found) {
                meta = found;
                return true;
            }
            return false;
        });

        if (isBooleanActionKey(key, meta) && typeof value === 'boolean') {
            value = value ? 1 : 0;
        }

        return {
            web_action: key,
            value,
            operator_code: global.operatorCode
        };
    });
};

const getChangedValues = (current, lastSaved) => {
    if (!lastSaved || typeof lastSaved !== 'object') return { ...current };
    const changed = {};
    Object.keys(current || {}).forEach((key) => {
        const c = current[key];
        const s = lastSaved[key];
        if (c !== s && (c !== undefined || s !== undefined)) changed[key] = c;
    });
    return changed;
};

const getFieldLabel = (item) => {
    if (item.description && item.description.trim()) return item.description;
    const name = item.action_name || '';
    return name
        .toLowerCase()
        .split('_')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
};

const isBooleanLike = (item) =>
    item.sub_category === 'Conditional' ||
    /^HIDE_|^SHOW_/i.test(item.action_name || '');

export const OperatorWebsiteSettingsTab = ({ initialValues, onValuesChange, active, webConfigActions = {} }) => {
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);

    const initialConfig = useMemo(
        () => buildDefaultConfigFromActions(webConfigActions, initialValues || {}),
        [webConfigActions, initialValues]
    );

    const [values, setValues] = useState(initialConfig);
    const [lastSavedValues, setLastSavedValues] = useState(initialConfig);

    useEffect(() => {
        if (active) {
            form.setFieldsValue(values);
        }
    }, [active, values, form]);

    useEffect(() => {
        const next = buildDefaultConfigFromActions(webConfigActions, values);
        setValues(next);
        form.setFieldsValue(next);
    }, [webConfigActions]);

    const handleSave = useCallback(() => {
        form.validateFields()
            .then((formValues) => {
                const merged = { ...values, ...formValues };
                const changed = getChangedValues(merged, lastSavedValues);
                if (Object.keys(changed).length === 0) {
                    message.info('No changes to save.');
                    return;
                }
                setSaving(true);
                setValues(merged);
                setLastSavedValues(merged);
                const payload = formValuesToApiPayload(changed, webConfigActions, initialValues);
                updateWebActionMapping(payload, () => {
                    setSaving(false);
                    message.success('Config saved successfully.');
                }, (err) => {
                    message.error(err || 'Failed to save config');
                    setSaving(false);
                });
            })
            .catch(() => {});
    }, [form, webConfigActions, values, lastSavedValues, initialValues]);

    const categories = useMemo(
        () =>
            Object.entries(webConfigActions || {}).map(([categoryName, items]) => ({
                categoryName,
                items: items || [],
            })),
        [webConfigActions]
    );

    return (
        <div className="tab-content tab-content-config">
            <p className="tab-description">
                Configure web actions grouped by category. Toggle conditional flags and set values as needed.
            </p>
            <Form
                form={form}
                layout="vertical"
                initialValues={values}
                onValuesChange={(_, formValues) => {
                    const next = { ...values, ...formValues };
                    setValues(next);
                    if (onValuesChange) onValuesChange(next);
                }}
                className="config-form config-form-fields"
            >
                {categories.map(({ categoryName, items }) => (
                    <div key={categoryName} className="config-category-block">
                        <h3 className="config-category-title">{categoryName}</h3>
                        <Row gutter={[24, 16]}>
                            {items.map((item) => {
                                const fieldName = item.action_name;
                                if (!fieldName) return null;
                                const label = getFieldLabel(item);
                                const booleanField = isBooleanLike(item);
                                const isWebVersionField = fieldName === 'WEB_VERSION';
                                return (
                                    <Col xs={24} md={12} key={fieldName}>
                                        <Form.Item
                                            name={fieldName}
                                            label={label}
                                            valuePropName={booleanField ? 'checked' : undefined}
                                        >
                                            {booleanField ? (
                                                <Switch checkedChildren="On" unCheckedChildren="Off" />
                                            ) : isWebVersionField ? (
                                                <Select placeholder={item.description || label} style={{ width: '100%' }}>
                                                    <Select.Option value={2}>2</Select.Option>
                                                    <Select.Option value={3}>3</Select.Option>
                                                </Select>
                                            ) : (
                                                <Input placeholder={item.description || label} />
                                            )}
                                        </Form.Item>
                                    </Col>
                                );
                            })}
                        </Row>
                    </div>
                ))}
                <div className="form-actions">
                    <Button type="primary" size="large" onClick={handleSave} loading={saving}>
                        Save Config
                    </Button>
                </div>
            </Form>
        </div>
    );
};
